import { useCallback, useEffect, useState } from "react";
import { CompareSlider } from "@/components/CompareSlider";
import { CursorMark } from "@/components/CursorMark";
import { Intro } from "@/components/Intro";
import { Controls } from "@/components/Controls";
import { Dropzone, type SourceImage } from "@/components/Dropzone";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { ProcessingPanel } from "@/components/ProcessingPanel";
import { ToastViewport } from "@/components/Toast";
import { useToast } from "@/hooks/useToast";
import { useUpscale } from "@/hooks/useUpscale";
import {
  copyBlobToClipboard,
  downloadBlob,
  estimateOutputSize,
  extensionForType,
  formatBytes,
  formatPixels,
  nowStamp,
  readImageMeta,
  type ScaleOption,
  type UpscaleMode,
  WARN_INPUT_EDGE,
} from "@/lib/utils";

const TAPE = [
  "NO SERVER",
  "NO BILL",
  "JPG PNG WEBP",
  "2× 4× 8×",
  "ESRGAN IN-TAB",
  "10 MB MAX",
  "YOUR GPU",
];

const FAQ = [
  {
    q: "Where does the picture go?",
    a: "Nowhere. The model downloads once, then everything runs in this browser. Close the tab and it's gone.",
  },
  {
    q: "Why is 8× slow or blocked?",
    a: "8× explodes pixel count. Phones choke. If the result would pass ~24 MP we refuse it. Use 2× or crop.",
  },
  {
    q: "Is this as good as paid upscalers?",
    a: "It's Real-ESRGAN, not a giant cloud model. Fine for photos, screenshots, and drawings. Soft on tiny 64px icons.",
  },
  {
    q: "Do I need an account?",
    a: "No. No token, no login, no card. Hosting is free. The work happens on your machine.",
  },
];

export default function App() {
  const { notify } = useToast();
  const upscale = useUpscale();
  const [source, setSource] = useState<SourceImage | null>(null);
  const [scale, setScale] = useState<ScaleOption>(4);
  const [mode, setMode] = useState<UpscaleMode>("photo");
  const [faceEnhance, setFaceEnhance] = useState(true);
  const [copying, setCopying] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [intro, setIntro] = useState(true);

  const busy =
    upscale.status === "submitting" || upscale.status === "processing";
  const hasResult = upscale.status === "succeeded" && Boolean(upscale.resultUrl);

  const handleSelect = useCallback(
    async (file: File) => {
      try {
        const meta = await readImageMeta(file);
        setSource((prev) => {
          if (prev) URL.revokeObjectURL(prev.previewUrl);
          return {
            file,
            previewUrl: meta.src,
            width: meta.width,
            height: meta.height,
          };
        });
        upscale.reset();
        if (Math.max(meta.width, meta.height) > WARN_INPUT_EDGE) {
          notify(
            "info",
            "Big plate",
            `${formatPixels(meta.width, meta.height)} will grind. Try 2×.`,
          );
        } else {
          notify("success", "Loaded", file.name);
        }
      } catch {
        notify("error", "Bad file", "Need a real JPG, PNG, or WEBP.");
      }
    },
    [notify, upscale],
  );

  const handleClear = useCallback(() => {
    if (source) URL.revokeObjectURL(source.previewUrl);
    setSource(null);
    upscale.reset();
  }, [source, upscale]);

  const handleRun = useCallback(async () => {
    if (!source) {
      notify("error", "Empty press", "Drop a picture first.");
      return;
    }
    try {
      await upscale.upscale({
        file: source.file,
        previewUrl: source.previewUrl,
        scale,
        mode,
        faceEnhance: mode === "photo" && faceEnhance,
      });
      notify("success", "Printed", "Drag the split. Save it.");
    } catch (error) {
      notify(
        "error",
        "Jam",
        error instanceof Error ? error.message : "Upscale failed.",
      );
    }
  }, [faceEnhance, mode, notify, scale, source, upscale]);

  const handleDownload = useCallback(() => {
    if (!upscale.resultBlob) return;
    const ext = extensionForType(upscale.resultBlob.type);
    downloadBlob(upscale.resultBlob, `sonarimg-${scale}x-${nowStamp()}.${ext}`);
    notify("success", "Saved", "Check downloads.");
  }, [notify, scale, upscale.resultBlob]);

  const handleCopy = useCallback(async () => {
    if (!upscale.resultBlob) return;
    setCopying(true);
    try {
      await copyBlobToClipboard(upscale.resultBlob);
      notify("success", "Copied", "On the clipboard.");
    } catch (error) {
      notify(
        "error",
        "Clipboard no",
        error instanceof Error ? error.message : "Browser blocked it.",
      );
    } finally {
      setCopying(false);
    }
  }, [notify, upscale.resultBlob]);

  const estimate = source
    ? estimateOutputSize(source.width, source.height, scale)
    : null;

  const endIntro = useCallback(() => {
    setIntro(false);
    document.body.classList.remove("intro-lock");
  }, []);

  useEffect(() => {
    if (intro) document.body.classList.add("intro-lock");
    return () => document.body.classList.remove("intro-lock");
  }, [intro]);

  return (
    <div className="cardboard min-h-screen text-ink">
      {intro ? <Intro onDone={endIntro} /> : null}
      <Header busy={busy} />
      <CursorMark />
      <ToastViewport />

      <section className="relative overflow-hidden border-b-[3px] border-ink bg-crate text-cream">
        <span className="pointer-events-none absolute -left-6 top-24 hidden animate-floaty border-[3px] border-ink bg-acid px-3 py-1 font-slam text-2xl uppercase text-ink sm:block">
          Fragile
        </span>
        <span className="pointer-events-none absolute right-6 top-16 hidden animate-floaty-r border-[3px] border-ink bg-tape px-3 py-1 font-slam text-xl uppercase text-ink md:block">
          This side up
        </span>
        <span className="pointer-events-none absolute bottom-24 right-10 hidden h-10 w-36 barcode opacity-40 lg:block" />

        <div className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 sm:py-14">
          <div className="flex flex-wrap gap-2">
            <span className="stamp animate-pop bg-acid text-ink">Lot 002</span>
            <span className="stamp animate-pop delay-b bg-tape text-ink">
              Local only
            </span>
            <span className="stamp animate-pop delay-d bg-cream text-ink">
              No account
            </span>
          </div>
          <h1 className="mt-6 max-w-5xl font-slam text-[clamp(3.2rem,11vw,8.2rem)] uppercase leading-[0.82]">
            {"SMALL PIC.".split("").map((ch, i) => (
              <span
                key={`a${i}`}
                className="letter"
                style={{ animationDelay: `${i * 35}ms` }}
              >
                {ch === " " ? "\u00A0" : ch}
              </span>
            ))}
            <br />
            {"BIG PIC.".split("").map((ch, i) => (
              <span
                key={`b${i}`}
                className="letter text-acid"
                style={{ animationDelay: `${220 + i * 35}ms` }}
              >
                {ch === " " ? "\u00A0" : ch}
              </span>
            ))}
          </h1>
          <p className="mt-6 max-w-xl text-base font-medium leading-relaxed text-cream/75 sm:text-lg">
            Slam a still through ESRGAN in this tab. 2×, 4×, 8×. Nothing
            leaves the machine. No cloud bill because there is no cloud.
          </p>
        </div>
        <div className="overflow-hidden border-t-[3px] border-ink bg-acid py-2 text-ink">
          <div className="flex min-w-full shrink-0 animate-marquee gap-8 pr-8">
            {[...TAPE, ...TAPE].map((item, i) => (
              <span
                key={`${item}-${i}`}
                className="font-slam text-xl uppercase tracking-wide"
              >
                {item} <span className="text-ink/40">/</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1200px] px-4 py-8 sm:px-6 sm:py-12">
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <section className="relative min-w-0">
            {!hasResult ? (
              <div className="relative">
                <Dropzone
                  source={source}
                  disabled={busy}
                  onSelect={(file) => void handleSelect(file)}
                  onClear={handleClear}
                  onInvalid={(message) => notify("error", "Rejected", message)}
                />
                <ProcessingPanel
                  visible={busy}
                  label={upscale.label}
                  elapsedMs={upscale.elapsedMs}
                  progress={upscale.progress}
                />
              </div>
            ) : (
              <div className="space-y-3">
                {source && upscale.resultUrl ? (
                  <CompareSlider
                    beforeSrc={source.previewUrl}
                    afterSrc={upscale.resultUrl}
                  />
                ) : null}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="btn-primary"
                  >
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleCopy()}
                    disabled={copying}
                    className="btn-ghost"
                  >
                    {copying ? "Copying" : "Copy"}
                  </button>
                  <button type="button" onClick={handleClear} className="btn-ghost">
                    New plate
                  </button>
                  <span className="ml-auto font-mono text-[10px] uppercase">
                    {upscale.elapsedMs
                      ? `${(upscale.elapsedMs / 1000).toFixed(1)}s`
                      : null}
                  </span>
                </div>
              </div>
            )}

            {source && !hasResult ? (
              <dl className="mt-3 grid grid-cols-3 gap-2">
                <Stat k="IN" v={formatPixels(source.width, source.height)} />
                <Stat k="KG" v={formatBytes(source.file.size)} />
                <Stat
                  k="OUT"
                  v={
                    estimate
                      ? formatPixels(estimate.width, estimate.height)
                      : "—"
                  }
                />
              </dl>
            ) : null}
          </section>

          <Controls
            scale={scale}
            mode={mode}
            faceEnhance={faceEnhance}
            source={source}
            busy={busy}
            canRun={Boolean(source) && !busy}
            engine={upscale.engine}
            onScale={setScale}
            onMode={setMode}
            onFaceEnhance={setFaceEnhance}
            onRun={() => void handleRun()}
          />
        </div>

        <section className="mt-12 grid gap-3 md:grid-cols-4">
          {[
            ["01", "Drop", "File stays in the tab. We never get a copy."],
            ["02", "Pick", "2× is safe. 4× is default. 8× is a dare."],
            ["03", "Wait", "Your GPU does the job. Phones take longer."],
            ["04", "Keep", "Split, download, or copy. Then dump the plate."],
          ].map(([n, t, d]) => (
            <article
              key={n}
              className="panel animate-pop p-4"
              style={{ animationDelay: `${Number(n) * 70}ms` }}
            >
              <p className="font-mono text-[10px] uppercase">{n}</p>
              <p className="mt-2 font-slam text-3xl uppercase leading-none">{t}</p>
              <p className="mt-2 text-sm font-medium leading-relaxed">{d}</p>
            </article>
          ))}
        </section>

        <section className="mt-3 grid gap-3 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="panel overflow-hidden">
            <div className="border-b-[3px] border-ink bg-ink px-4 py-3 text-cream">
              <p className="font-slam text-2xl uppercase">Spec sheet</p>
            </div>
            <table className="w-full text-left text-sm">
              <tbody>
                {[
                  ["Engine", "ESRGAN slim, TensorFlow.js"],
                  ["Where", "Your browser. WebGL if it exists."],
                  ["Formats", "JPG, PNG, WEBP"],
                  ["Limit", "10 MB in. ~24 MP out."],
                  ["Scales", "2× / 4× / 8× (8× = 4× then 2×)"],
                  ["Cost", "Rp 0. Always."],
                ].map(([k, v]) => (
                  <tr key={k} className="border-b-[3px] border-ink last:border-0">
                    <th className="w-[34%] bg-paper px-4 py-3 font-mono text-[11px] uppercase">
                      {k}
                    </th>
                    <td className="px-4 py-3 font-medium">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="panel overflow-hidden">
            <div className="border-b-[3px] border-ink bg-tape px-4 py-3">
              <p className="font-slam text-2xl uppercase">FAQ</p>
            </div>
            {FAQ.map((item, index) => {
              const open = openFaq === index;
              return (
                <button
                  key={item.q}
                  type="button"
                  onClick={() => setOpenFaq(open ? -1 : index)}
                  className="block w-full border-b-[3px] border-ink px-4 py-3 text-left last:border-0"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="font-slam text-lg uppercase leading-none">
                      {item.q}
                    </span>
                    <span className="font-mono text-xs">{open ? "−" : "+"}</span>
                  </span>
                  {open ? (
                    <span className="mt-2 block text-sm font-medium leading-relaxed">
                      {item.a}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }) {
  return (
    <div className="border-[3px] border-ink bg-cream px-2 py-2 shadow-smash-sm">
      <dt className="font-mono text-[10px] uppercase">{k}</dt>
      <dd className="truncate font-slam text-lg uppercase leading-none">{v}</dd>
    </div>
  );
}
